import React, { useState, useEffect, useRef, useCallback } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Badge } from "./ui/badge";
import { ScrollArea } from "./ui/scroll-area";
import {
  MessageCircle,
  X,
  Send,
  Users,
  Hash,
  ChevronLeft,
  Circle,
  Search,
  Plus,
  AlertTriangle,
  ShoppingCart,
  Factory,
  Bell,
} from "lucide-react";

const API = process.env.REACT_APP_BACKEND_URL + "/api";
const WS_URL = process.env.REACT_APP_BACKEND_URL?.replace("https://", "wss://").replace("http://", "ws://") + "/api/chat/ws";

const CHANNEL_ICONS = {
  hash: Hash,
  factory: Factory,
  "shopping-cart": ShoppingCart,
  "alert-triangle": AlertTriangle,
  users: Users,
};

export default function ChatBubble({ user }) {
  const [isOpen, setIsOpen] = useState(false);
  const [view, setView] = useState("list"); // list, chat, users
  const [channels, setChannels] = useState([]);
  const [conversations, setConversations] = useState([]);
  const [availableUsers, setAvailableUsers] = useState([]);
  const [selectedChat, setSelectedChat] = useState(null);
  const [messages, setMessages] = useState([]);
  const [newMessage, setNewMessage] = useState("");
  const [unreadCounts, setUnreadCounts] = useState({ total: 0, by_conversation: {} });
  const [isConnected, setIsConnected] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");
  const [isTyping, setIsTyping] = useState(null);
  
  const wsRef = useRef(null);
  const messagesEndRef = useRef(null);
  const reconnectTimeoutRef = useRef(null);
  const typingTimeoutRef = useRef(null);

  // Scroll to bottom of messages
  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  };

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  // Connect WebSocket
  const connectWebSocket = useCallback(() => {
    if (!user?.id) return;
    
    const token = localStorage.getItem("mes_token");
    if (!token) return;

    const ws = new WebSocket(`${WS_URL}/${user.id}?token=${token}`);
    
    ws.onopen = () => {
      console.log("Chat WebSocket connected");
      setIsConnected(true);
    };
    
    ws.onmessage = (event) => {
      const data = JSON.parse(event.data);
      
      if (data.type === "new_message") {
        const msg = data.message;
        
        // Adicionar mensagem se for da conversa atual
        if (selectedChat?.id === msg.conversation_id) {
          setMessages(prev => [...prev, msg]);
          // Marcar como lida
          markAsRead(msg.conversation_id);
        } else {
          // Atualizar contagem de não lidas
          setUnreadCounts(prev => ({
            total: prev.total + 1,
            by_conversation: {
              ...prev.by_conversation,
              [msg.conversation_id]: (prev.by_conversation[msg.conversation_id] || 0) + 1
            }
          }));
          
          // Mostrar notificação
          if (msg.sender_id !== user.id) {
            // Tocar som
            playNotificationSound();
            
            // Toast notification
            toast.info(`${msg.sender_name}: ${msg.content.substring(0, 50)}...`, {
              action: {
                label: "Ver",
                onClick: () => {
                  setIsOpen(true);
                  openChat({ id: msg.conversation_id, name: msg.sender_name });
                }
              }
            });
          }
        }
      } else if (data.type === "typing") {
        if (selectedChat?.id === data.conversation_id && data.user_id !== user.id) {
          setIsTyping(data.user_name);
          clearTimeout(typingTimeoutRef.current);
          typingTimeoutRef.current = setTimeout(() => setIsTyping(null), 3000);
        }
      }
    };
    
    ws.onclose = () => {
      console.log("Chat WebSocket disconnected");
      setIsConnected(false);
      // Tentar reconectar após 5 segundos
      reconnectTimeoutRef.current = setTimeout(connectWebSocket, 5000);
    };
    
    ws.onerror = (error) => {
      console.error("Chat WebSocket error:", error);
    };
    
    wsRef.current = ws;
  }, [user?.id, selectedChat?.id]);

  // Disconnect WebSocket on unmount
  useEffect(() => {
    connectWebSocket();
    
    return () => {
      if (wsRef.current) {
        wsRef.current.close();
      }
      if (reconnectTimeoutRef.current) {
        clearTimeout(reconnectTimeoutRef.current);
      }
    };
  }, [connectWebSocket]);

  // Fetch data when chat opens
  useEffect(() => {
    if (isOpen && user?.id) {
      fetchChannels();
      fetchConversations();
      fetchUnreadCounts();
    }
  }, [isOpen, user?.id]);

  // Fetch channels
  const fetchChannels = async () => {
    try {
      const res = await axios.get(`${API}/chat/channels?app_source=mes`);
      setChannels(res.data);
      
      // Auto-join default channels
      for (const channel of res.data) {
        if (channel.is_default && !channel.members?.includes(user.id)) {
          try {
            await axios.post(`${API}/chat/channels/${channel.id}/join?user_id=${user.id}`);
          } catch (e) {
            // Ignore join errors
          }
        }
      }
      
      // Refresh channels after joining
      const refreshed = await axios.get(`${API}/chat/channels?app_source=mes`);
      setChannels(refreshed.data);
    } catch (error) {
      console.error("Error fetching channels:", error);
    }
  };

  // Fetch conversations
  const fetchConversations = async () => {
    try {
      const res = await axios.get(`${API}/chat/conversations?user_id=${user.id}`);
      setConversations(res.data);
    } catch (error) {
      console.error("Error fetching conversations:", error);
    }
  };

  // Fetch unread counts
  const fetchUnreadCounts = async () => {
    try {
      const res = await axios.get(`${API}/chat/unread?user_id=${user.id}`);
      setUnreadCounts(res.data);
    } catch (error) {
      console.error("Error fetching unread counts:", error);
    }
  };

  // Fetch available users
  const fetchAvailableUsers = async () => {
    try {
      const res = await axios.get(`${API}/chat/users/available?current_user_id=${user.id}`);
      setAvailableUsers(res.data);
    } catch (error) {
      console.error("Error fetching users:", error);
    }
  };

  // Fetch messages
  const fetchMessages = async (conversationId) => {
    try {
      const res = await axios.get(`${API}/chat/messages/${conversationId}?limit=100`);
      setMessages(res.data);
    } catch (error) {
      console.error("Error fetching messages:", error);
    }
  };

  // Mark messages as read
  const markAsRead = async (conversationId) => {
    try {
      await axios.post(`${API}/chat/messages/mark-read?conversation_id=${conversationId}&user_id=${user.id}`);
      setUnreadCounts(prev => ({
        total: Math.max(0, prev.total - (prev.by_conversation[conversationId] || 0)),
        by_conversation: {
          ...prev.by_conversation,
          [conversationId]: 0
        }
      }));
    } catch (error) {
      console.error("Error marking as read:", error);
    }
  };

  // Open chat
  const openChat = (chat) => {
    setSelectedChat(chat);
    setView("chat");
    fetchMessages(chat.id);
    markAsRead(chat.id);
  };

  // Send message
  const sendMessage = async () => {
    if (!newMessage.trim() || !selectedChat) return;
    
    const messageData = {
      action: "send_message",
      conversation_id: selectedChat.id,
      content: newMessage.trim(),
      sender_name: user.name,
      sender_role: user.role,
      app_source: "mes"
    };
    
    if (wsRef.current && wsRef.current.readyState === WebSocket.OPEN) {
      wsRef.current.send(JSON.stringify(messageData));
      setNewMessage("");
    } else {
      // Fallback to REST API
      try {
        await axios.post(
          `${API}/chat/messages?user_id=${user.id}&user_name=${encodeURIComponent(user.name)}&user_role=${user.role}&app_source=mes`,
          {
            conversation_id: selectedChat.id,
            content: newMessage.trim(),
            message_type: "text"
          }
        );
        setNewMessage("");
        fetchMessages(selectedChat.id);
      } catch (error) {
        console.error("Error sending message:", error);
        toast.error("Erro ao enviar mensagem");
      }
    }
  };

  // Start new conversation
  const startConversation = async (otherUser) => {
    try {
      const res = await axios.post(
        `${API}/chat/conversations?user_id=${user.id}&user_name=${encodeURIComponent(user.name)}`,
        { participant_id: otherUser.id }
      );
      openChat({ ...res.data, name: otherUser.name });
    } catch (error) {
      console.error("Error starting conversation:", error);
      toast.error("Erro ao iniciar conversa");
    }
  };

  // Join channel
  const joinChannel = async (channel) => {
    try {
      await axios.post(`${API}/chat/channels/${channel.id}/join?user_id=${user.id}`);
      fetchChannels();
      openChat({ ...channel, name: channel.name, type: "channel" });
    } catch (error) {
      console.error("Error joining channel:", error);
    }
  };

  // Send typing indicator
  const sendTyping = () => {
    if (wsRef.current && wsRef.current.readyState === WebSocket.OPEN && selectedChat) {
      wsRef.current.send(JSON.stringify({
        action: "typing",
        conversation_id: selectedChat.id,
        user_name: user.name
      }));
    }
  };

  // Play notification sound
  const playNotificationSound = () => {
    try {
      const audio = new Audio("data:audio/wav;base64,UklGRnoGAABXQVZFZm10IBAAAAABAAEAQB8AAEAfAAABAAgAZGF0YQoGAACBhYqFbF1fdJivrJBhNjVgodDbq2EcBj+a2teleQYTYIbj9cV9Jw5eeejxi0khbZzT44xSJFWq4+WBPxR2uc7OfUEYgMfH0X5BGoTHwtF/QxuEycHRf0MdhMrA0X9DHoTLwNF/QiKGzL7PfUEkh86929BvKijH1tPKVyYywdbTy1koMMTX1M5aJi7D2NbRXCQswdnW0V0jKsHZ1tFdIynA2tbRXiMpv9rW0V8jKb/a1tFfIym/2tXRXyMpv9rV0V8jKb/a1dFfIym/2tXRXyMowtrU0V8gJ8fa09JeICfH2tLSXiAmx9rR014gJsjb0NJdICXI29DSXSAlydvP0l0gJcnbz9JdICTK28/SXSAkytvP0l0gJMrbz9JdICTK28/SXSAkydvP0l0gJMnbz9JdICTJ28/SXQ==");
      audio.volume = 0.3;
      audio.play().catch(() => {});
    } catch (e) {}
  };

  // Handle key press
  const handleKeyPress = (e) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      sendMessage();
    }
  };

  // Get channel icon component
  const getChannelIcon = (iconName) => {
    const IconComponent = CHANNEL_ICONS[iconName] || Hash;
    return IconComponent;
  };

  // Filter chats by search
  const filteredChannels = channels.filter(c => 
    c.name.toLowerCase().includes(searchQuery.toLowerCase())
  );
  const filteredConversations = conversations.filter(c => 
    c.participant_names?.some(n => n.toLowerCase().includes(searchQuery.toLowerCase()))
  );

  if (!user) return null;

  return (
    <>
      {/* Chat Bubble Button */}
      <div className="fixed bottom-6 right-6 lg:right-24 z-50">
        <Button
          onClick={() => setIsOpen(!isOpen)}
          className="h-14 w-14 rounded-full shadow-lg bg-blue-600 hover:bg-blue-700 relative"
          data-testid="chat-bubble-btn"
        >
          {isOpen ? (
            <X className="h-6 w-6" />
          ) : (
            <>
              <MessageCircle className="h-6 w-6" />
              {unreadCounts.total > 0 && (
                <span className="absolute -top-1 -right-1 h-5 w-5 rounded-full bg-red-500 text-xs text-white flex items-center justify-center">
                  {unreadCounts.total > 99 ? "99+" : unreadCounts.total}
                </span>
              )}
            </>
          )}
        </Button>
      </div>

      {/* Chat Window */}
      {isOpen && (
        <div 
          className="fixed bottom-24 right-2 left-2 lg:left-auto lg:right-24 lg:w-96 h-[calc(100vh-160px)] lg:h-[500px] bg-white rounded-2xl shadow-2xl border border-gray-200 flex flex-col overflow-hidden z-50"
          data-testid="chat-window"
        >
          {/* Header */}
          <div className="bg-blue-600 text-white p-4 flex items-center gap-3">
            {view !== "list" && (
              <Button
                variant="ghost"
                size="icon"
                className="h-8 w-8 text-white hover:bg-blue-500"
                onClick={() => { setView("list"); setSelectedChat(null); }}
              >
                <ChevronLeft className="h-5 w-5" />
              </Button>
            )}
            <div className="flex-1">
              <h3 className="font-semibold">
                {view === "list" && "Chat"}
                {view === "chat" && selectedChat?.name}
                {view === "users" && "Nova Conversa"}
              </h3>
              {view === "chat" && isTyping && (
                <p className="text-xs text-blue-200">{isTyping} está a escrever...</p>
              )}
            </div>
            <div className="flex items-center gap-2">
              <Circle className={`h-2 w-2 ${isConnected ? "fill-green-400 text-green-400" : "fill-red-400 text-red-400"}`} />
              {view === "list" && (
                <Button
                  variant="ghost"
                  size="icon"
                  className="h-8 w-8 text-white hover:bg-blue-500"
                  onClick={() => { setView("users"); fetchAvailableUsers(); }}
                >
                  <Plus className="h-5 w-5" />
                </Button>
              )}
            </div>
          </div>

          {/* Content */}
          <div className="flex-1 overflow-hidden">
            {/* List View */}
            {view === "list" && (
              <div className="h-full flex flex-col">
                {/* Search */}
                <div className="p-2 border-b">
                  <div className="relative">
                    <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
                    <Input
                      placeholder="Pesquisar..."
                      value={searchQuery}
                      onChange={(e) => setSearchQuery(e.target.value)}
                      className="pl-9 h-9"
                    />
                  </div>
                </div>
                
                <ScrollArea className="flex-1">
                  {/* Channels */}
                  {filteredChannels.length > 0 && (
                    <div className="p-2">
                      <p className="text-xs font-semibold text-gray-500 px-2 mb-1">CANAIS</p>
                      {filteredChannels.map(channel => {
                        const IconComponent = getChannelIcon(channel.icon);
                        const unread = unreadCounts.by_conversation[channel.id] || 0;
                        return (
                          <div
                            key={channel.id}
                            className="flex items-center gap-3 p-2 rounded-lg hover:bg-gray-100 cursor-pointer"
                            onClick={() => channel.members?.includes(user.id) ? openChat({ ...channel, type: "channel" }) : joinChannel(channel)}
                          >
                            <div 
                              className="h-10 w-10 rounded-lg flex items-center justify-center"
                              style={{ backgroundColor: channel.color + "20" }}
                            >
                              <IconComponent className="h-5 w-5" style={{ color: channel.color }} />
                            </div>
                            <div className="flex-1 min-w-0">
                              <p className="font-medium truncate">{channel.name}</p>
                              <p className="text-xs text-gray-500 truncate">{channel.last_message || channel.description}</p>
                            </div>
                            {unread > 0 && (
                              <Badge className="bg-red-500 text-white">{unread}</Badge>
                            )}
                          </div>
                        );
                      })}
                    </div>
                  )}
                  
                  {/* Direct Messages */}
                  {filteredConversations.length > 0 && (
                    <div className="p-2">
                      <p className="text-xs font-semibold text-gray-500 px-2 mb-1">MENSAGENS DIRETAS</p>
                      {filteredConversations.map(conv => {
                        const otherName = conv.participant_names?.find((_, i) => conv.participants[i] !== user.id) || "Utilizador";
                        const unread = unreadCounts.by_conversation[conv.id] || 0;
                        return (
                          <div
                            key={conv.id}
                            className="flex items-center gap-3 p-2 rounded-lg hover:bg-gray-100 cursor-pointer"
                            onClick={() => openChat({ ...conv, name: otherName, type: "private" })}
                          >
                            <div className="h-10 w-10 rounded-full bg-gray-200 flex items-center justify-center">
                              <span className="text-lg font-semibold text-gray-600">
                                {otherName.charAt(0).toUpperCase()}
                              </span>
                            </div>
                            <div className="flex-1 min-w-0">
                              <p className="font-medium truncate">{otherName}</p>
                              <p className="text-xs text-gray-500 truncate">{conv.last_message || "Sem mensagens"}</p>
                            </div>
                            {unread > 0 && (
                              <Badge className="bg-red-500 text-white">{unread}</Badge>
                            )}
                          </div>
                        );
                      })}
                    </div>
                  )}
                  
                  {filteredChannels.length === 0 && filteredConversations.length === 0 && (
                    <div className="p-8 text-center text-gray-400">
                      <MessageCircle className="h-12 w-12 mx-auto mb-2 opacity-50" />
                      <p>Nenhuma conversa encontrada</p>
                    </div>
                  )}
                </ScrollArea>
              </div>
            )}

            {/* Chat View */}
            {view === "chat" && selectedChat && (
              <div className="h-full flex flex-col">
                <ScrollArea className="flex-1 p-4">
                  <div className="space-y-3">
                    {messages.map((msg, idx) => {
                      const isOwn = msg.sender_id === user.id;
                      const showAvatar = idx === 0 || messages[idx - 1]?.sender_id !== msg.sender_id;
                      
                      return (
                        <div
                          key={msg.id}
                          className={`flex ${isOwn ? "justify-end" : "justify-start"}`}
                        >
                          <div className={`max-w-[80%] ${isOwn ? "items-end" : "items-start"} flex flex-col`}>
                            {showAvatar && !isOwn && (
                              <span className="text-xs text-gray-500 ml-1 mb-1">{msg.sender_name}</span>
                            )}
                            <div
                              className={`px-3 py-2 rounded-2xl ${
                                isOwn 
                                  ? "bg-blue-600 text-white rounded-br-md" 
                                  : "bg-gray-100 text-gray-800 rounded-bl-md"
                              }`}
                            >
                              <p className="text-sm whitespace-pre-wrap break-words">{msg.content}</p>
                            </div>
                            <span className="text-[10px] text-gray-400 mt-0.5 mx-1">
                              {new Date(msg.created_at).toLocaleTimeString("pt-PT", { hour: "2-digit", minute: "2-digit" })}
                              {msg.app_source !== "mes" && (
                                <span className="ml-1 text-blue-400">({msg.app_source})</span>
                              )}
                            </span>
                          </div>
                        </div>
                      );
                    })}
                    <div ref={messagesEndRef} />
                  </div>
                </ScrollArea>
                
                {/* Message Input */}
                <div className="p-3 border-t">
                  <div className="flex gap-2">
                    <Input
                      placeholder="Escreva uma mensagem..."
                      value={newMessage}
                      onChange={(e) => { setNewMessage(e.target.value); sendTyping(); }}
                      onKeyPress={handleKeyPress}
                      className="flex-1"
                      data-testid="chat-input"
                    />
                    <Button
                      onClick={sendMessage}
                      disabled={!newMessage.trim()}
                      className="bg-blue-600 hover:bg-blue-700"
                      data-testid="chat-send-btn"
                    >
                      <Send className="h-4 w-4" />
                    </Button>
                  </div>
                </div>
              </div>
            )}

            {/* Users View */}
            {view === "users" && (
              <ScrollArea className="h-full">
                <div className="p-2">
                  <p className="text-xs font-semibold text-gray-500 px-2 mb-2">UTILIZADORES</p>
                  {availableUsers.map(u => (
                    <div
                      key={u.id}
                      className="flex items-center gap-3 p-2 rounded-lg hover:bg-gray-100 cursor-pointer"
                      onClick={() => startConversation(u)}
                    >
                      <div className="h-10 w-10 rounded-full bg-gray-200 flex items-center justify-center relative">
                        <span className="text-lg font-semibold text-gray-600">
                          {u.name?.charAt(0).toUpperCase() || "?"}
                        </span>
                        {u.is_online && (
                          <span className="absolute bottom-0 right-0 h-3 w-3 bg-green-500 rounded-full border-2 border-white" />
                        )}
                      </div>
                      <div className="flex-1">
                        <p className="font-medium">{u.name}</p>
                        <p className="text-xs text-gray-500">{u.role === "admin" ? "Administrador" : "Operador"}</p>
                      </div>
                    </div>
                  ))}
                  {availableUsers.length === 0 && (
                    <div className="p-8 text-center text-gray-400">
                      <Users className="h-12 w-12 mx-auto mb-2 opacity-50" />
                      <p>Nenhum utilizador disponível</p>
                    </div>
                  )}
                </div>
              </ScrollArea>
            )}
          </div>
        </div>
      )}
    </>
  );
}
